import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  inject,
  input,
  output,
} from '@angular/core';
import { FormControl, NgControl, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { UpdateFormHasBeenInView } from '@app/sales/states/form/form.actions';
import { IconNames } from '@app/shared/enums/icon-names';
import { InputWidth } from '@app/shared/models/input-width.model';
import { Store } from '@ngxs/store';
import { IceIconButtonComponent } from '../../atoms/ice-icon-button/ice-icon-button.component';
import { MatIcon } from '@angular/material/icon';
import { NgClass } from '@angular/common';
import { NgxMaskDirective } from 'ngx-mask';
import { getObjectPropertyFromPath } from '@app/core/utils';

/**
 * 'ngDefaultControl' and 'formControlName' must be provided to the component.
 *
 * Usage example as standalone input:
 * @example
 * <app-ice-input-field ngDefaultControl formControlName="myFormControlName" label="myLabel" />
 *
 * Usage example as part of a form:
 * @example
 * <app-ice-input-field ngDefaultControl formControlName="myFormControlName" label="myLabel" ngxsFormPath="stateForm.myForm" />
 */
@Component({
  selector: 'app-ice-input-field',
  templateUrl: './ice-input-field.component.html',
  styleUrl: './ice-input-field.component.scss',
  imports: [NgClass, MatIcon, FormsModule, ReactiveFormsModule, IceIconButtonComponent, NgxMaskDirective],
})
export class IceInputFieldComponent implements OnInit, AfterViewInit, OnDestroy {
  private controlDirective = inject(NgControl);
  private store = inject(Store);

  IconNames = IconNames;

  @ViewChild('inputElement') inputElement: ElementRef;

  // Label
  @Input({ required: true }) label: string;
  @Input() labelHelpText: string;
  @Input() forId = '';

  // Input
  @Input() inputWidth: InputWidth = '';
  @Input() type: 'text' | 'password' | 'number' | 'tel' | 'email' | 'date' = 'text';
  @Input() placeholder = '';
  @Input() readonly = false;
  @Input() ngxsFormPath: string;
  @Input() minLength: number;
  @Input() maxLength: number;
  @Input() focusOnLoad = false;
  @Input() selectOnFocus = false;
  blurEvent = output<void>();

  // Information
  @Input() errorText: string | null;
  @Input() longHelpText: string;

  // Icons
  @Input() prefixIcon: IconNames;
  @Input() suffixIcon: IconNames;
  @Output() suffixIconClickedAction = new EventEmitter<void>();

  // Error state
  @Input() errorOnTouchedOnly: boolean = false;
  @Input() showErrorOnLoad: boolean = false;

  // Testing
  textTestId = input.required<string>();
  buttonTestId = input<string>();

  // Formatting
  mask = input<string>();

  @HostBinding('class') get class() {
    return this.inputWidth;
  }

  control: FormControl;

  get isDisabled(): boolean {
    return this.control.disabled;
  }

  get isRequired(): boolean {
    return this.control.hasValidator(Validators.required);
  }

  get showIsRequiredError(): boolean {
    return this.control.hasError('required') && this.isFormVisitedOrControlTouched();
  }

  get isErrorState(): boolean {
    return (
      (this.control.invalid && !!this.control.value && (this.control.touched || this.isFormVisitedOrControlTouched())) ||
      this.showIsRequiredError
    );
  }

  private hasFormBeenVisited = false;

  ngAfterViewInit(): void {
    if (this.focusOnLoad) {
      this.inputElement?.nativeElement.focus();
    }
  }

  ngOnInit() {
    this.control = this.controlDirective.control as FormControl;

    if (!!this.ngxsFormPath) {
      const state = this.store.snapshot();
      const contextForm = getObjectPropertyFromPath(state, this.ngxsFormPath);
      this.hasFormBeenVisited = contextForm?.dirty;
    }

    if (this.showErrorOnLoad) {
      this.hasFormBeenVisited = true;
    }
  }

  isFormVisitedOrControlTouched(): boolean {
    return this.errorOnTouchedOnly ? this.control.touched : this.hasFormBeenVisited;
  }

  onSuffixIconClicked(): void {
    if (this.control.disabled || this.readonly) {
      return;
    }
    this.suffixIconClickedAction.emit();
  }

  onFocus(event) {
    if (this.selectOnFocus) {
      event.target.select();
    }
  }

  ngOnDestroy() {
    if (!!this.ngxsFormPath) {
      this.store.dispatch(new UpdateFormHasBeenInView(this.ngxsFormPath));
    }
  }
}
